import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useDistributors } from '@/hooks/useDistributors';
import DistributorModal from './components/DistributorModal';
import { type Distributor } from '@/services/distributors';
import { fetchDistributorConsignments, type DistributorConsignmentSummary } from '@/services/consignments';
import ConsignmentDetailModal from '@/pages/admin/consignments/components/ConsignmentDetailModal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const CREDIT_META: Record<string, { label: string; cls: string }> = {
  'Al día': { label: 'Al día', cls: 'text-emerald-700 bg-emerald-50' },
  'Pendiente de pago': { label: 'Pendiente de pago', cls: 'text-amber-700 bg-amber-50' },
  'Pago parcial': { label: 'Pago parcial', cls: 'text-orange-700 bg-orange-50' },
};

function creditStatus(raw: string): string {
  if (!raw || raw === 'active') return 'Al día';
  return CREDIT_META[raw] ? raw : 'Al día';
}

export default function DistributorsPage() {
  const { data, loading, error, refetch } = useDistributors();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Distributor | null>(null);
  const [consignFor, setConsignFor] = useState<Distributor | null>(null);
  const [consignList, setConsignList] = useState<DistributorConsignmentSummary[]>([]);
  const [loadingConsign, setLoadingConsign] = useState(false);
  const [detailId, setDetailId] = useState('');

  async function openConsignments(d: Distributor) {
    setConsignFor(d);
    setDetailId('');
    setLoadingConsign(true);
    setConsignList(await fetchDistributorConsignments(d.id));
    setLoadingConsign(false);
  }

  const filtered = data.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.businessName.toLowerCase().includes(q) ||
      d.contactName.toLowerCase().includes(q) ||
      d.city.toLowerCase().includes(q) ||
      d.email.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Distribuidores</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} distribuidores registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo Distribuidor
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Distribuidores</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Descuento promedio</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              {data.length
                ? `${(data.reduce((s, d) => s + d.discountRate, 0) / data.length).toFixed(1)}%`
                : '0%'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Ciudades</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">
              {new Set(data.map((d) => d.city).filter(Boolean)).size}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por negocio, contacto o ciudad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Negocio</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Contacto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Ciudad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Descuento</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Crédito</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Alta</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando distribuidores...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((d) => (
                    <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-store-2-line text-accent-600"></i>
                          </div>
                          <div>
                            <p className="text-foreground-900 font-medium">{d.businessName}</p>
                            <p className="text-xs text-foreground-500">{d.documentId || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-700">{d.contactName || '—'}</td>
                      <td className="px-5 py-3 text-foreground-600">{d.city || '—'}</td>
                      <td className="px-5 py-3 text-right font-medium text-foreground-900 whitespace-nowrap">
                        {d.discountRate}%
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${CREDIT_META[creditStatus(d.creditStatus)].cls}`}>
                          <i className={`ri-circle-fill text-[6px] ${creditStatus(d.creditStatus) === 'Al día' ? '' : 'animate-pulse'}`}></i>
                          {CREDIT_META[creditStatus(d.creditStatus)].label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(d.createdAt)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openConsignments(d)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Ver consignaciones"
                          >
                            <i className="ri-git-repository-line text-foreground-400"></i>
                          </button>
                          <button
                            onClick={() => setEditing(d)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            aria-label="Editar distribuidor"
                          >
                            <i className="ri-more-2-fill text-foreground-400"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-store-2-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay distribuidores registrados.'
                  : 'No se encontraron distribuidores con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showNew && <DistributorModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {editing && (
        <DistributorModal distributor={editing} onClose={() => setEditing(null)} onSaved={refetch} />
      )}
      {consignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConsignFor(null)}></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl border border-background-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-foreground-950">Consignaciones</h2>
                <p className="text-sm text-foreground-500 mt-0.5">{consignFor.businessName}</p>
              </div>
              <button onClick={() => setConsignFor(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            {loadingConsign ? (
              <div className="text-center py-8 text-foreground-400 text-sm">Cargando...</div>
            ) : consignList.length === 0 ? (
              <div className="text-center py-8 text-foreground-400 text-sm">Sin consignaciones registradas.</div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {consignList.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-lg px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{c.itemCount} pares entregados</p>
                      <p className="text-xs text-foreground-500">
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                    </div>
                    <button
                      onClick={() => setDetailId(c.id)}
                      className="text-xs px-3 py-1.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Ver factura
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {detailId && (
        <ConsignmentDetailModal
          consignmentId={detailId}
          onClose={() => setDetailId('')}
          onSaved={refetch}
        />
      )}
    </AdminLayout>
  );
}